import type { MapDocument } from "../document/types.ts";
import { shiftedRect, type AreaOffset, type AreaRect } from "./area.ts";

/**
 * An area's pending move (ADR-128 decisions 16, 17 and 51): the offset in whole tiles a move waits with until it is
 * confirmed, and whether the block is picked up and carried under the pointer. Carrying and dragging hold the
 * destination inside the map; a typed distance and a step do not, so their plan can refuse at the edge and say so
 * (decision 24). View state: nothing here changes the map.
 */

/**
 * What the pending move needs: the selected rectangle, the map in view and the store's notification.
 */
export interface AreaPendingHost {
  readonly rect: () => AreaRect | null;
  readonly map: () => MapDocument;
  readonly notify: () => void;
}

/**
 * The pending move's offset and whether it is carried.
 */
export class AreaPending {
  private pending: AreaOffset | null = null;
  private held = false;

  /**
   * @param host The area's view of itself.
   */
  public constructor(private readonly host: AreaPendingHost) {}

  /**
   * The pending offset, `dy` down, or null while no move is pending.
   *
   * @returns The offset.
   */
  public get offset(): AreaOffset | null {
    return this.pending;
  }

  /**
   * Whether the block is picked up.
   *
   * @returns True while carried.
   */
  public get carried(): boolean {
    return this.held;
  }

  /**
   * Where the pending move would land.
   *
   * @returns The destination, or null while no move is pending.
   */
  public get destination(): AreaRect | null {
    const rect = this.host.rect();
    return rect === null || this.pending === null ? null : shiftedRect(rect, this.pending);
  }

  /**
   * Picks the selection up; a move already pending keeps its offset.
   *
   * @returns False with nothing selected.
   */
  public pickUp(): boolean {
    if (this.host.rect() === null) {
      return false;
    }
    this.held = true;
    this.pending = this.pending ?? { dx: 0, dy: 0 };
    this.host.notify();
    return true;
  }

  /**
   * Carries the block so that its centre tile is the one given, held inside the map. Does nothing unless the block
   * is picked up.
   *
   * @param tile The tile under the pointer; it may lie off the map.
   */
  public carryTo(tile: { readonly x: number; readonly y: number }): void {
    const rect = this.host.rect();
    if (rect === null || !this.held) {
      return;
    }
    this.place(this.heldInside({ dx: Math.floor(tile.x) - Math.floor((rect.x0 + rect.x1 + 1) / 2), dy: Math.floor(tile.y) - Math.floor((rect.y0 + rect.y1 + 1) / 2) }));
  }

  /**
   * Puts the block down where it is carried, still pending.
   */
  public putDown(): void {
    if (this.held) {
      this.held = false;
      this.host.notify();
    }
  }

  /**
   * Sets the offset from a drag, held so the destination stays inside the map. A carried area is put down.
   *
   * @param offset The offset in tiles.
   */
  public dragTo(offset: AreaOffset): void {
    if (this.host.rect() !== null) {
      const was = this.held;
      this.held = false;
      if (!this.place(this.heldInside(offset)) && was) {
        this.host.notify();
      }
    }
  }

  /**
   * Sets the offset as typed, not held to the map. A carried area is put down. An offset that is not two whole
   * numbers (a field cleared, a letter typed, a fraction) is refused and changes nothing.
   *
   * @param offset The offset in tiles.
   * @returns False when the offset was refused or nothing is selected.
   */
  public setOffset(offset: AreaOffset): boolean {
    if (this.host.rect() === null || !Number.isInteger(offset.dx) || !Number.isInteger(offset.dy)) {
      return false;
    }
    const was = this.held;
    this.held = false;
    if (!this.place({ dx: offset.dx + 0, dy: offset.dy + 0 }) && was) {
      this.host.notify();
    }
    return true;
  }

  /**
   * Ends the pending move, telling no one: the caller notifies.
   *
   * @returns True when a move was pending or carried.
   */
  public end(): boolean {
    const was = this.pending !== null || this.held;
    this.pending = null;
    this.held = false;
    return was;
  }

  /**
   * Sets the offset, telling subscribers only when it changed.
   *
   * @param offset The offset.
   * @returns True when it changed and subscribers were told.
   */
  private place(offset: AreaOffset): boolean {
    if (this.pending !== null && this.pending.dx === offset.dx && this.pending.dy === offset.dy) {
      return false;
    }
    this.pending = offset;
    this.host.notify();
    return true;
  }

  /**
   * An offset held so that the selection's destination lies inside the map, in whole tiles, a negative zero
   * written as zero.
   *
   * @param offset The offset.
   * @returns The held offset.
   */
  private heldInside(offset: AreaOffset): AreaOffset {
    const rect = this.host.rect();
    if (rect === null) {
      return offset;
    }
    const map = this.host.map();
    return { dx: Math.max(-rect.x0, Math.min(map.width - 1 - rect.x1, Math.round(offset.dx))) + 0, dy: Math.max(-rect.y0, Math.min(map.height - 1 - rect.y1, Math.round(offset.dy))) + 0 };
  }
}
